import * as React from "react";
import { useAuth } from "@/auth/AuthProvider";
import { useAction } from "@/lib/useAsync";
import { NEW_PASSWORD_HINT, PASSWORDS_DIFFER } from "@/auth/password-rules";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { Notice } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/modal";

/**
 * Changing one's own password, from the top bar.
 *
 * The CURRENT password is asked for even though the operator is signed in: a
 * session left open on a shared laptop must not be enough to lock its owner
 * out. The worker closes every other session and hands this one a new token,
 * so the operator stays where they were.
 *
 * "They do not match" is `PASSWORDS_DIFFER`; `password-rules.ts` says why only
 * a browser checks it.
 */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { changePassword } = useAuth();
  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [again, setAgain] = React.useState("");
  const [done, setDone] = React.useState(false);
  const action = useAction();
  const { reset } = action;

  const clear = React.useCallback(() => {
    setCurrent("");
    setNext("");
    setAgain("");
  }, []);

  // A closed dialog forgets what was typed: a password left in a hidden form
  // is one the next person to open it can submit.
  React.useEffect(() => {
    if (open) return;
    clear();
    reset();
    setDone(false);
  }, [open, clear, reset]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (next !== again) return action.fail(PASSWORDS_DIFFER);
    const changed = await action.run(() => changePassword(current, next));
    if (changed) {
      setDone(true);
      clear();
    }
  }

  return (
    // Not closable while the change is in flight: its answer would land in a
    // dialog that had already forgotten the question.
    <Modal open={open} title="Change your password" onClose={action.busy ? () => {} : onClose}>
      {done ? (
        <div className="space-y-4">
          <Notice tone="success">
            Your password is changed. Every other session with this account has been signed out.
          </Notice>
          <div className="flex justify-end">
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Current password" htmlFor="pw-current">
            <Input
              id="pw-current"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field label="New password" htmlFor="pw-next" hint={NEW_PASSWORD_HINT}>
            <Input
              id="pw-next"
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label="New password again" htmlFor="pw-again">
            <Input
              id="pw-again"
              type="password"
              autoComplete="new-password"
              required
              value={again}
              onChange={(e) => setAgain(e.target.value)}
            />
          </Field>
          {action.error && <Notice tone="error">{action.error}</Notice>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={action.busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={action.busy || !current || !next || !again}>
              {action.busy ? "Working…" : "Change password"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
